import React, { useState } from 'react'
import styled from 'styled-components'
import { Button, Popover } from 'antd'
import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import {
  getTaskKindConfig,
  FALLBACK_KIND_CONFIG,
} from '../config/task-kind-registry'
import { KindIcon } from './KindIcon'
import { PRIORITY_GROUPS } from '../constants/priority-groups'
import { SnoozePicker } from './SnoozePicker.lazy'
import type { Task } from '../schemas/task'

export type TaskBulletProps = {
  task: Task
  isPending?: boolean
  onPrimary: (task: Task) => void
  onReplanifier: (task: Task, chosenDate: Date) => void
  onIgnore: (task: Task) => void
}

const Row = styled.div`
  display: grid;
  grid-template-columns: 8px 22px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 14px;
  border-radius: 6px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(22, 119, 255, 0.05);
  }

  &:hover .actions {
    opacity: 1;
  }
`

const PriorityDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 8px;
  background: ${(p) => p.$color};
  flex: none;
`

const KindBadge = styled.span<{ $fg: string; $bg: string }>`
  width: 22px;
  height: 22px;
  border-radius: 5px;
  flex: none;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const Title = styled.span`
  font-size: 12.5px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.88);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Actions = styled.span`
  display: inline-flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
`

export const TaskBullet: React.FC<TaskBulletProps> = ({
  task,
  isPending = false,
  onPrimary,
  onReplanifier,
  onIgnore,
}) => {
  const config = getTaskKindConfig(task.kind)
  const isFallback = config === FALLBACK_KIND_CONFIG
  const isManual = task.source === 'manual'
  const Meta = PRIORITY_GROUPS.find((g) => g.id === task.bucket)
  const [snoozeOpen, setSnoozeOpen] = useState(false)

  const applyReschedule = (date: Date) => {
    setSnoozeOpen(false)
    onReplanifier(task, date)
  }

  const reschedulePopover = (
    <SnoozePicker
      width={220}
      onPick={applyReschedule}
      onCancel={() => setSnoozeOpen(false)}
    />
  )

  const handleRowClick = () => {
    if (isPending || isFallback) return
    onPrimary(task)
  }

  return (
    <Row data-cy={`assistant--task-bullet_${task.kind}`} onClick={handleRowClick}>
      <PriorityDot $color={Meta?.accentColor ?? 'rgba(0,0,0,0.25)'} />
      <KindBadge $fg={config.accentColor} $bg={config.accentBg}>
        <KindIcon icon={config.icon} color={config.accentColor} size={12} />
      </KindBadge>
      <Title>{task.actionLabelOverride ?? task.title}</Title>
      <Actions className="actions" onClick={(e) => e.stopPropagation()}>
        {/* Valider — manual tasks only (FR-041 / FR-042). Auto tasks are
            completed exclusively by the engine. */}
        {isManual && (
          <Button
            size="small"
            type="text"
            disabled={isFallback || isPending}
            loading={isPending}
            icon={<CheckOutlined />}
            onClick={() => onPrimary(task)}
            aria-label={config.actionLabel}
            data-cy={`action--assistant_primary_${task.kind}`}
          />
        )}
        {/* Replanifier — available on every task (FR-041, FR-042). */}
        <Popover
          open={snoozeOpen}
          onOpenChange={(v) => !isPending && setSnoozeOpen(v)}
          content={reschedulePopover}
          trigger="click"
          placement="topRight"
          destroyOnHidden
        >
          <Button
            size="small"
            type="text"
            title="Replanifier"
            aria-label="Replanifier"
            disabled={isPending}
            icon={<ClockCircleOutlined />}
            data-cy={`action--assistant_replanifier_${task.id}`}
          />
        </Popover>
        {/* Ignorer — available on every task. One-way; dismissed tasks are
            filtered out by the panel query. */}
        <Button
          size="small"
          type="text"
          title="Ignorer"
          aria-label="Ignorer"
          disabled={isPending}
          icon={<CloseOutlined />}
          onClick={() => onIgnore(task)}
          data-cy={`action--assistant_ignore_${task.id}`}
        />
      </Actions>
    </Row>
  )
}
